import { Link } from '@tanstack/react-router'
import type { EligibilityEvaluationView, EligibilityResult } from '@tex/finance-shared'
import { useState } from 'react'
import { describeRule } from '../lib/rule-form'
import { Alert, Button, Card, Mono } from './ui'
import { errorMessage } from '../lib/api'
import { formatDate, humanize } from '../lib/format'
import { useEligibilityEvaluations, useEvaluateEligibility } from '../lib/programs'

const resultTone: Record<EligibilityResult, string> = {
  eligible: 'bg-green-100 text-green-900',
  ineligible: 'bg-red-100 text-red-900',
  manual_review: 'bg-amber-100 text-amber-900',
}

function ResultBadge({ result }: { result: EligibilityResult }) {
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${resultTone[result]}`}>{humanize(result)}</span>
}

/** Latest evaluation per program version, newest first. */
function latestPerProgram(evaluations: EligibilityEvaluationView[]): EligibilityEvaluationView[] {
  const seen = new Set<string>()
  const out: EligibilityEvaluationView[] = []
  for (const e of evaluations) {
    if (seen.has(e.program_version_id)) continue
    seen.add(e.program_version_id)
    out.push(e)
  }
  return out
}

/** Tex-only: partner programs and evaluations never appear in dealer or borrower views. */
export function EligibilityCard({ caseId }: { caseId: string }) {
  const evaluations = useEligibilityEvaluations(caseId, true)
  const evaluate = useEvaluateEligibility(caseId)
  const [showAll, setShowAll] = useState(false)

  const all = evaluations.data?.evaluations ?? []
  const shown = showAll ? all : latestPerProgram(all)

  return (
    <Card
      title="Eligibility"
      actions={
        <div className="flex items-center gap-2">
          {all.length > 0 && (
            <button type="button" className="text-xs text-neutral-500 hover:underline" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Latest per program' : `Show all (${all.length})`}
            </button>
          )}
          <Button className="!px-2 !py-0.5 text-xs" disabled={evaluate.isPending} onClick={() => evaluate.mutate()}>
            Evaluate now
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {evaluations.isError && <Alert tone="error">{errorMessage(evaluations.error)}</Alert>}
        {evaluate.isError && <Alert tone="error">{errorMessage(evaluate.error)}</Alert>}
        {evaluations.data && all.length === 0 && (
          <p className="text-sm text-neutral-500">
            Not evaluated yet. Evaluation runs every active partner program against the facts on this request and records an immutable snapshot.
          </p>
        )}
        {shown.map((e) => (
          <EvaluationRow key={e.id} evaluation={e} />
        ))}
      </div>
    </Card>
  )
}

function EvaluationRow({ evaluation: e }: { evaluation: EligibilityEvaluationView }) {
  const [open, setOpen] = useState(false)
  const unknown = Object.entries(e.facts)
    .filter(([, f]) => f.status === 'unknown')
    .map(([k]) => k)
  return (
    <div className="rounded-md border border-[var(--border-subtle)] p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-medium">{e.partner_display_name}</span>{' '}
          <Link to="/tex/programs/$programId" params={{ programId: e.program_version_id }} className="text-neutral-500 hover:underline">
            <Mono>{e.program_code}</Mono> v{e.program_version}
          </Link>
          <span className="text-neutral-500"> · {e.program_title}</span>
        </div>
        <ResultBadge result={e.result} />
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {e.reasons.length === 0 ? (
          <span className="text-xs text-neutral-500">all rules satisfied on known facts</span>
        ) : (
          e.reasons.map((r) => (
            <span key={r} className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs text-neutral-700">
              {r}
            </span>
          ))
        )}
      </div>
      <p className="mt-1 text-xs text-neutral-500">
        Evaluated {formatDate(e.evaluated_at)}
        {e.revision_id ? ' against the submitted application' : ' on lead facts only (no application yet)'}
        {unknown.length > 0 && ` · unknown: ${unknown.join(', ')}`}
      </p>
      <button type="button" className="mt-1 text-xs text-neutral-500 hover:underline" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide snapshot' : 'Show rules and facts as evaluated'}
      </button>
      {open && (
        <div className="mt-2 grid gap-3 border-t border-neutral-100 pt-2 text-xs sm:grid-cols-2">
          <div>
            <h4 className="mb-1 font-semibold text-neutral-700">Jurisdiction checks</h4>
            <ul className="space-y-0.5">
              {e.jurisdiction_checks.map((j) => (
                <li key={j.field}>
                  {j.field}: <Mono>{j.state ?? '—'}</Mono> → {j.status}
                </li>
              ))}
            </ul>
            <h4 className="mb-1 mt-2 font-semibold text-neutral-700">Rules ({e.rules_snapshot.length})</h4>
            <ul className="space-y-0.5">
              {e.rule_evaluations.map((r, i) => (
                <li key={i}>
                  <span className={r.result === 'fail' ? 'text-tex-red' : r.result === 'unknown' ? 'text-amber-800' : ''}>{r.result}</span> ·{' '}
                  {e.rules_snapshot[i] ? describeRule(e.rules_snapshot[i]) : `${r.field} ${r.operator}`}
                  {r.actual !== null && (
                    <>
                      {' '}
                      (actual <Mono>{Array.isArray(r.actual) ? r.actual.join(', ') : String(r.actual)}</Mono>, {r.evidence})
                    </>
                  )}
                </li>
              ))}
              {e.rule_evaluations.length === 0 && <li className="text-neutral-500">not reached</li>}
            </ul>
          </div>
          <div>
            <h4 className="mb-1 font-semibold text-neutral-700">Facts</h4>
            <ul className="space-y-0.5">
              {Object.entries(e.facts).map(([k, f]) => (
                <li key={k}>
                  <Mono>{k}</Mono>:{' '}
                  {f.status === 'known' ? (
                    <>
                      {Array.isArray(f.value) ? f.value.join(', ') : String(f.value)} <span className="text-neutral-500">({f.evidence})</span>
                    </>
                  ) : (
                    <span className="text-amber-800">unknown</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
